import { useWindowDimensions, Platform } from "react-native";

/**
 * Global design tokens — warm light theme (Archimedes / Figma "Create 01").
 *
 * Figma variable collection modes resolved for mode 1358:1.
 * All component style files should import from here.
 */

export const colors = {
  /* ---- Backgrounds ---- */
  bg: "#FFF9E7",              /* Surface → brand/Lychee */
  /* Game Lab cream — sampled from the template-selection design so the coder page,
     the picker plate and any letterboxing are all the same colour. */
  gameLabCream: "#FCF0DE",
  surface: "#FAFAF8",         /* NB-Neutral-50 — cards, panels */
  surfaceHover: "#F0EFEB",    /* NB-Neutral-100 — hover / pressed */
  surfaceLight: "#E5E4E0",    /* NB-Neutral-200 — subtle dividers */

  /* ---- Text ---- */
  text: "#130A45",            /* On Surface → Grape-900 */
  textSecondary: "#3D2B7D",   /* Grape-700 — secondary copy */
  textMuted: "#A8A69E",       /* NB-Neutral-400 — labels, hints */
  textDim: "#C4C3BD",         /* NB-Neutral-300 — disabled / placeholder */

  /* ---- Accent ---- */
  primary: "#130A45",         /* Button/bg-normal → Grape-900 */
  accent: "#8356DC",          /* Grape-500 — links, highlights */

  /* ---- Entity ---- */
  character: "#7C3AED",
  scene: "#0D9488",
  beat: "#EA580C",

  /* ---- Border ---- */
  border: "#E5E4E0",          /* NB-Neutral-200 */
  borderLight: "#F0EFEB",     /* NB-Neutral-100 */
  borderFocus: "#6243ED",     /* Purple — focused beat border */
  borderBeat: "#5D46CE",      /* Outline accent — non-focused beat border */
  labelSelected: "#9B82F3",   /* Selected label text on dark backgrounds */

  /* ---- Status ---- */
  danger: "#B02D33",
  success: "#30D158",
  warning: "#FFD60A",

  /* ---- Base ---- */
  white: "#FFFFFF",
  black: "#000000",
  transparent: "transparent",

  /* ---- Style selector ---- */
  styleDropdownBg: "#080422",     /* Grape-950 — style panel background */
  styleSelectedBorder: "#FFB732", /* Gold — selected style highlight */

  /* ---- Gallery (dark theme) ---- */
  surfaceDark: "#181A2A",         /* Dark surface — gallery, book view */
  galleryBg: "#181A2A",           /* Alias for surfaceDark */
  btnSecondary: "#5E5D90",       /* Secondary button bg — book view, back buttons */
  galleryCard: "#262842",         /* Surface-container — card background */
  galleryCardCoverBg: "#1E2040", /* Fallback cover background */
  galleryOnSurface: "#FBF8FE",   /* On-surface text on dark cards */
  galleryBtnBg: "#CED0FB",       /* Button normal — pill buttons on gallery cards */
  checkboxOutline: "#6E80CA",    /* Unchecked checkbox border — gallery delete mode */
  galleryCloseBtnBg: "#DDD4FC",   /* Purple close button */
  progressBorder: "#E2B6F8",      /* Purple/pink — progress button pill border */

  /* ---- Toast ---- */
  toastLightBg: "#EBE2C8",       /* Surface Container Bright — light theme */
  toastLightText: "#1E2034",     /* On Surface — light theme */
  toastDarkBg: "#2C285C",        /* Surface Container Bright — dark theme */
  toastDarkTitle: "#EEEFFC",     /* On Surface — dark theme */
  toastDarkBody: "#CED0FB",      /* On Surface Variant — dark theme */
  toastPurpleBg: "#272453",      /* Neogrape 180 — purple theme */
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
};

export const fontSize = {
  xs: 10,
  sm: 12,
  md: 14,
  lg: 18,
  xl: 24,
  xxl: 32,
};

export const borderRadius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  round: 9999,
};

export const fontFamily = {
  // Web: CSS @font-face family name (weight selected via fontWeight).
  // Android: TTF file name (without extension). For Ember Display, each weight is a
  // separate file. For Alexa Sans, a single variable font handles all weights —
  // callers MUST set fontWeight alongside fontFamily for correct rendering on Android.
  display: Platform.OS === "web" ? "Amazon Ember Display" : "AmazonEmberDisplay_Rg",
  displayMedium: Platform.OS === "web" ? "Amazon Ember Display" : "AmazonEmberDisplay_Md",
  displayBold: Platform.OS === "web" ? "Amazon Ember Display" : "AmazonEmberDisplay_Bd",
  text: Platform.OS === "web" ? "Alexa Sans Text" : "Alexa-Sans-VF",
  textMedium: Platform.OS === "web" ? "Alexa Sans Text" : "Alexa-Sans-VF",
  textBold: Platform.OS === "web" ? "Alexa Sans Text" : "Alexa-Sans-VF",
};

/** Reusable text style presets — spread into StyleSheet definitions. */
export const textStyle = {
  body: { fontFamily: fontFamily.text, fontWeight: "400" as const },
  bodyMedium: { fontFamily: fontFamily.textMedium, fontWeight: "500" as const },
  bodyBold: { fontFamily: fontFamily.textBold, fontWeight: "700" as const },
  heading: { fontFamily: fontFamily.displayBold, fontWeight: "700" as const },
  headingMedium: { fontFamily: fontFamily.displayMedium, fontWeight: "500" as const },
};

/** Full-screen absolute overlay, centered content. Yoga 3 compatible. */
export const centeredOverlay = {
  position: "absolute" as const,
  top: 0,
  left: 0,
  width: "100%" as const,
  height: "100%" as const,
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

/** Shared back button style (48px circle, btnSecondary bg, white icon). */
export const backButtonStyle = {
  container: {
    width: 48, height: 48, borderRadius: 24,
    backgroundColor: colors.btnSecondary,
    alignItems: "center" as const, justifyContent: "center" as const,
  },
  icon: { width: 24, height: 24, tintColor: colors.white },
};

/**
 * Design reference width. On screens narrower than this, UI scales down.
 * On screens >= this width, scale is capped at 1.
 */
export const DESIGN_WIDTH = 1200;

export function useScale() {
  const { width } = useWindowDimensions();
  const scale = Math.min(1, width / DESIGN_WIDTH);
  const s = (value: number) => Math.round(value * scale);
  return { scale, s };
}

/**
 * VoiceOrb display size for a given scale factor: 116px on wide screens, easing
 * down to 0.7× as the screen narrows. One size for every Muse instance (landing,
 * FTUE, story stage) so the orb never changes size across transitions. 116px is
 * the Figma story-stage Muse (~0.80× the 144px card width).
 */
export function getOrbSize(scale: number): number {
  return Math.round(116 * Math.min(1, 0.7 + scale * 0.3));
}

/**
 * Figma design dimensions (unscaled). Use with `s()` from `useScale()`.
 * Named after their Figma component / role so the source is traceable.
 */
export const design = {
  /* Header buttons */
  headerBtn: { width: 80, height: 80, radius: 30 },
  headerBtnNarrow: { width: 64, height: 80, radius: 30 },
  headerNotch: { width: 80, height: 124 },
  stylePill: { height: 80, minWidth: 200, radius: 30, previewSize: 80 },
  styleGrid: { imgSize: 110, itemSlot: 126, radius: 30, borderW: 8, margin: 8, padding: 24, gap: 24, columns: 4, fontSize: 14, dropdownTop: 110, dropdownPaddingRight: 80 },
  iconSize: 28,
  galleryIconSize: 36,

  /* Entity cards (Image Item) */
  entityCard: { width: 144, padding: 8, radius: 18 },
  entityImage: { size: 128, radius: 14 },
  entityLabel: { fontSize: 16 },

  /* Card stage — "Characters" / "Places" section header */
  sectionLabel: { fontSize: 18 },

  /* Muse (voice orb + face) */
  orbSize: 130,
  orbSizeActive: 130,

  /* Generate button */
  generateBtn: { minWidth: 176, paddingV: 16, paddingH: 20, radius: 32, fontSize: 16, marginTop: 12 },

  /* Story beats — sized down from the original 327x184 so the caption fits
     below the card within the ~30% band and more cards span the screen. */
  beat: { width: 167, height: 94, gap: 40 },
  beatFocused: { width: 240, height: 135, borderWidth: 4, borderRadius: 24 },
  beatText: { fontSize: 16, lineHeight: 24 },
  // Beats take a flat fraction of the window height (Figma ≈ 30%: 33.9% on the
  // large frame, 28.8% on the small one) rather than a width-scaled fixed px, so
  // cards + Muse keep the larger share of the screen as in the design.
  beatsSection: { heightFrac: 0.3 },

  /* Typography */
  stylePillLabel: { fontSize: 14 },
  stylePillValue: { fontSize: 20 },
};
